import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Alert,
  CircularProgress,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Stack,
  Link,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Replay as ReopenIcon,
} from '@mui/icons-material';
import privateLessonRequestService from '../../services/privateLessonRequestService';
// [3. javító kör, F-1, 2026-10-03] lejárat-tudatos burkolók (`utils/saveErrors.ts`)
import { requestErrorMessage, saveErrorMessage } from '../../utils/saveErrors';
import { useFeedback } from '../../contexts/FeedbackContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useUnsavedChanges } from '../../contexts/UnsavedChangesContext';
import { isFormDirty, discardChangesConfirm } from '../../utils/formDirty';
import { useResponsive } from '../../hooks/useResponsive';
import { formatServerDateLong } from '../../utils/dateUtils';
import {
  PrivateLessonRequest,
  PrivateLessonRequestFilter,
} from '../../types/privateLessonRequest';

const STATUS_LABEL: Record<PrivateLessonRequest['status'], string> = {
  Pending: 'Függő',
  Handled: 'Elintézve',
  Rejected: 'Elutasítva',
};

const FILTER_LABEL: Record<PrivateLessonRequestFilter, string> = {
  Pending: 'Függő',
  Handled: 'Elintézett',
  Rejected: 'Elutasított',
  All: 'Mind',
};

const EMPTY_MESSAGE: Record<PrivateLessonRequestFilter, string> = {
  Pending: 'Nincs függő magánóra-igénylés.',
  Handled: 'Nincs elintézett magánóra-igénylés.',
  Rejected: 'Nincs elutasított magánóra-igénylés.',
  All: 'Még nem érkezett magánóra-igénylés.',
};

// A preferredDate dátum-only (YYYY-MM-DD) — ezt a `new Date(...)` UTC-ként parse-olná (NB-3),
// ami negatív időzóna-eltolásnál egy nappal korábbi dátumot mutathat; a közös segéd a napot
// zóna-tolás nélkül írja ki.
// [Web UX 1. csomag (4.3), 2026-10-03] JAVÍTOTT TÉVES FELTEVÉS: a korábbi komment szerint a createdDate/handledDate
// „'Z'-vel” érkezik. NEM: élő API-n MÉRVE (2026-10-03) mindkettő „Z” NÉLKÜL jön
// (pl. `"handledDate":"2026-10-03T15:20:30.426916"`; a szerver UTC-t ment —
// `PrivateLessonRequestsController.cs:111` —, és a `ToDto` nem ad `SpecifyKind`-et), ezért a
// `new Date(s)` helyi időnek vette, és 00:00–02:00 között az előző NAPOT írta ki. A közös segéd
// UTC-nek veszi és a budapesti napot írja ki.
const formatDate = (dateString?: string | null): string => formatServerDateLong(dateString);

const formatTime = (timeString?: string | null): string => (timeString ? timeString.substring(0, 5) : '');

const formatPreferred = (r: PrivateLessonRequest): string => {
  if (!r.preferredDate) return 'nincs megadva';
  const time = formatTime(r.preferredTime);
  return time ? `${formatDate(r.preferredDate)} ${time}` : formatDate(r.preferredDate);
};

interface PrivateLessonRequestsProps {
  /** A Beállítások fül-szintű Badge-hez: mindig a Függő darabszámot adja vissza (a szűrőtől függetlenül). */
  onPendingCountChange?: (count: number) => void;
}

const PrivateLessonRequests: React.FC<PrivateLessonRequestsProps> = ({ onPendingCountChange }) => {
  const { isSmallScreen } = useResponsive();
  const { showSuccess } = useFeedback();
  const [filter, setFilter] = useState<PrivateLessonRequestFilter>('Pending');
  const [requests, setRequests] = useState<PrivateLessonRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const [dialogRequest, setDialogRequest] = useState<PrivateLessonRequest | null>(null);
  const [dialogAction, setDialogAction] = useState<'Handled' | 'Rejected' | null>(null);
  const [adminNote, setAdminNote] = useState('');
  const [dialogError, setDialogError] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const refreshPendingCount = useCallback(async () => {
    if (!onPendingCountChange) return;
    try {
      const count = await privateLessonRequestService.getPendingCount();
      onPendingCountChange(count);
    } catch {
      // Nem kritikus — a fül-Badge egyszerűen nem frissül, a lista maga továbbra is működik.
    }
  }, [onPendingCountChange]);

  const loadRequests = useCallback(async (status: PrivateLessonRequestFilter) => {
    try {
      setLoading(true);
      setError('');
      const data = await privateLessonRequestService.getRequests(status);
      setRequests(data);
    } catch (err: any) {
      setError(requestErrorMessage(err, 'Hiba történt az igénylések betöltésekor.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRequests(filter);
  }, [filter, loadRequests]);

  useEffect(() => {
    refreshPendingCount();
  }, [refreshPendingCount]);

  const handleFilterChange = (event: SelectChangeEvent) => {
    setFilter(event.target.value as PrivateLessonRequestFilter);
  };

  const openDialog = (request: PrivateLessonRequest, action: 'Handled' | 'Rejected') => {
    setDialogRequest(request);
    setDialogAction(action);
    setAdminNote('');
    setDialogError('');
  };

  const closeDialog = () => {
    setDialogRequest(null);
    setDialogAction(null);
    setAdminNote('');
    setDialogError('');
  };

  /**
   * [Web UX 2. csomag – mentetlen-védelem (8.1), 2026-10-03] Lelet:
   * `PrivateLessonRequests.tsx:369-373` (korábbi sorok) — a játékosnak szóló „Admin megjegyzés”
   * a háttér-kattintásra/Esc-re/„Mégse”-re kérdés nélkül elveszett. Piszkos = nem üres (trim után);
   * az üres mező NEM kérdez.
   */
  const confirm = useConfirm();
  const dialogOpen = Boolean(dialogRequest && dialogAction);
  const dialogSaving = Boolean(dialogRequest && actionLoading === dialogRequest.privateLessonRequestId);
  const isNoteDirty = dialogOpen && isFormDirty({ adminNote: '' }, { adminNote });
  useUnsavedChanges(isNoteDirty, 'Admin megjegyzés');

  const requestCloseDialog = async () => {
    if (dialogSaving) return;
    if (isNoteDirty && !(await confirm(discardChangesConfirm('admin megjegyzés')))) return;
    closeDialog();
  };

  const confirmDialog = async () => {
    if (!dialogRequest || !dialogAction) return;
    try {
      setActionLoading(dialogRequest.privateLessonRequestId);
      setDialogError('');
      await privateLessonRequestService.updateStatus(dialogRequest.privateLessonRequestId, {
        status: dialogAction,
        adminNote: adminNote.trim() || undefined,
      });
      closeDialog();
      showSuccess(dialogAction === 'Handled' ? 'Az igénylés elintézve.' : 'Az igénylés elutasítva.');
      await loadRequests(filter);
      await refreshPendingCount();
    } catch (err: any) {
      setDialogError(saveErrorMessage(err, 'Nem sikerült frissíteni az igénylést.'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleReopen = async (r: PrivateLessonRequest) => {
    try {
      setActionLoading(r.privateLessonRequestId);
      await privateLessonRequestService.updateStatus(r.privateLessonRequestId, { status: 'Pending' });
      showSuccess('Az igénylés visszanyitva.');
      await loadRequests(filter);
      await refreshPendingCount();
    } catch (err: any) {
      setError(requestErrorMessage(err, 'Nem sikerült visszanyitni az igénylést.'));
    } finally {
      setActionLoading(null);
    }
  };

  const renderActions = (r: PrivateLessonRequest) => {
    if (r.status === 'Pending') {
      return (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', md: 'center' }, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="contained"
            color="success"
            startIcon={<ApproveIcon />}
            onClick={() => openDialog(r, 'Handled')}
            disabled={actionLoading === r.privateLessonRequestId}
          >
            Elintézve
          </Button>
          <Button
            size="small"
            variant="contained"
            color="error"
            startIcon={<RejectIcon />}
            onClick={() => openDialog(r, 'Rejected')}
            disabled={actionLoading === r.privateLessonRequestId}
          >
            Elutasítás
          </Button>
        </Box>
      );
    }
    return (
      <Box>
        <Typography variant="body2">
          {r.handledByName || '—'}
          {r.handledDate ? ` · ${formatDate(r.handledDate)}` : ''}
        </Typography>
        {r.adminNote ? (
          <Typography variant="caption" color="text.secondary">
            {r.adminNote}
          </Typography>
        ) : null}
        <Box sx={{ mt: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ReopenIcon />}
            onClick={() => handleReopen(r)}
            disabled={actionLoading === r.privateLessonRequestId}
          >
            Visszanyitás
          </Button>
        </Box>
      </Box>
    );
  };

  const contactInfo = (r: PrivateLessonRequest) => (
    <Stack spacing={0.25}>
      <Typography variant="body2">{r.playerName}</Typography>
      <Link href={`mailto:${r.playerEmail}`} variant="caption" underline="hover">
        {r.playerEmail}
      </Link>
      {r.playerPhone ? (
        <Link href={`tel:${r.playerPhone}`} variant="caption" underline="hover">
          {r.playerPhone}
        </Link>
      ) : null}
    </Stack>
  );

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">Magánóra-igénylések</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="plr-status-filter-label">Státusz</InputLabel>
            <Select
              labelId="plr-status-filter-label"
              label="Státusz"
              value={filter}
              onChange={handleFilterChange}
            >
              {(Object.keys(FILTER_LABEL) as PrivateLessonRequestFilter[]).map((key) => (
                <MenuItem key={key} value={key}>
                  {FILTER_LABEL[key]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={() => loadRequests(filter)} disabled={loading}>
            Frissítés
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : requests.length === 0 ? (
        <Paper sx={{ p: 3 }}>
          <Typography variant="body1" color="text.secondary" align="center">
            {EMPTY_MESSAGE[filter]}
          </Typography>
        </Paper>
      ) : isSmallScreen ? (
        <Stack spacing={2}>
          {requests.map((r) => (
            <Paper key={r.privateLessonRequestId} sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                {contactInfo(r)}
                <Chip
                  label={STATUS_LABEL[r.status]}
                  size="small"
                  color={r.status === 'Handled' ? 'success' : r.status === 'Rejected' ? 'error' : 'warning'}
                />
              </Box>
              <Typography variant="body2">
                <strong>Kívánt edző:</strong> {r.preferredCoachNames.length ? r.preferredCoachNames.join(', ') : 'bármelyik'}
              </Typography>
              <Typography variant="body2">
                <strong>Kívánt időpont:</strong> {formatPreferred(r)}
              </Typography>
              {r.note ? (
                <Typography variant="body2">
                  <strong>Megjegyzés:</strong> {r.note}
                </Typography>
              ) : null}
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                Beérkezett: {formatDate(r.createdDate)}
              </Typography>
              <Box sx={{ mt: 1.5 }}>{renderActions(r)}</Box>
            </Paper>
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Játékos</strong></TableCell>
                <TableCell><strong>Kívánt edző</strong></TableCell>
                <TableCell><strong>Kívánt időpont</strong></TableCell>
                <TableCell><strong>Megjegyzés</strong></TableCell>
                <TableCell><strong>Beérkezve</strong></TableCell>
                {filter !== 'Pending' && <TableCell><strong>Állapot</strong></TableCell>}
                <TableCell align="center"><strong>Műveletek / lezárás</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((r) => (
                <TableRow hover key={r.privateLessonRequestId}>
                  <TableCell>{contactInfo(r)}</TableCell>
                  <TableCell>{r.preferredCoachNames.length ? r.preferredCoachNames.join(', ') : 'bármelyik'}</TableCell>
                  <TableCell>{formatPreferred(r)}</TableCell>
                  <TableCell>{r.note || '-'}</TableCell>
                  <TableCell>{formatDate(r.createdDate)}</TableCell>
                  {filter !== 'Pending' && (
                    <TableCell>
                      <Chip
                        label={STATUS_LABEL[r.status]}
                        size="small"
                        color={r.status === 'Handled' ? 'success' : r.status === 'Rejected' ? 'error' : 'warning'}
                      />
                    </TableCell>
                  )}
                  <TableCell align="center">{renderActions(r)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={requestCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>{dialogAction === 'Handled' ? 'Igénylés elintézése' : 'Igénylés elutasítása'}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {dialogRequest ? `${dialogRequest.playerName} magánóra-igénylése — ${formatPreferred(dialogRequest)}.` : ''}
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Admin megjegyzés (opcionális)"
            value={adminNote}
            onChange={(e) => setAdminNote(e.target.value)}
            helperText="Ezt a megjegyzést a játékos látja az Igényléseim képernyőn."
            placeholder={
              dialogAction === 'Handled'
                ? 'Pl. „Kedden 17 órára beosztottunk Kovács edzőhöz.”'
                : 'Pl. „Sajnos ezen a héten nincs szabad időpont.”'
            }
          />
          {dialogError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {dialogError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={requestCloseDialog}>Mégse</Button>
          <Button
            variant="contained"
            color={dialogAction === 'Handled' ? 'success' : 'error'}
            onClick={confirmDialog}
            disabled={Boolean(dialogRequest && actionLoading === dialogRequest.privateLessonRequestId)}
          >
            {dialogAction === 'Handled' ? 'Elintézve' : 'Elutasítás'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PrivateLessonRequests;
